// Deposit feed.
//
// The status vocabulary here is the pipeline's, not the KYT verdict's — those
// are different things and conflating them was a real schema bug. `held` in
// particular means "we could not screen", which is retryable and must never be
// mistaken for "rejected".
//
// On the design system (22 Oct 2026): every step the money took is ONE line
// with its times; the reasons behind a rating sit behind Details.

import { SigninQuiet } from '../../components/v2/custody/SigninLine';
import { Lookalike, lookalikeOf } from '../../components/v2/custody/DepositKyt';
import { isCustodySignin } from '../../lib/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ReadFailed } from './ReadFailed';
import { custody as api, type DepositRow } from '../../lib/api';
import { money, shortAddress, relativeTime, POOL_WORD, CLASS_WORD } from '../../lib/custody-format';
import { Card, StateLine, EmptyState, Pending, Pill, Value } from '../../components/kit';
import { listLines, listedDeposits, cardReady, cardStateOf, windowOpenUntil, type CardState } from '../../lib/custody-screen';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/** The rating's result as said (N-011, 20 Oct 2026): where the money went. Older rows keep their old words. */
const RESULT_WORD: Record<string, string> = {
  clean: `clean → ${CLASS_WORD['running_hot']}`,
  exposed: `→ ${CLASS_WORD['dirty_hot']}`,
  pending: 'not rated yet',
};

type PillTone = 'neutral' | 'good' | 'warn' | 'bad';
const STATUS: Record<DepositRow['status'], { label: string; tone: PillTone; line?: string; more?: string }> = {
  detected:    { label: 'Detected',   tone: 'neutral' },
  confirmed:   { label: 'Confirmed',  tone: 'neutral' },
  screening:   { label: 'Screening',  tone: 'neutral' },
  held:        { label: 'Held',       tone: 'warn', line: 'Not rated yet — asked again every minute.', more: 'KYT has not rated this payment yet. It is not accepted until it is.' },
  routed:      { label: 'Routed',     tone: 'good' },
  swept:       { label: 'Swept',      tone: 'good' },
  quarantined: { label: 'Flagged',    tone: 'bad', line: 'Held in the Flagged wallet by your decision.', more: 'Never Hot. It moves only on your word.' },
  rejected:    { label: 'Rejected',   tone: 'bad', line: 'Did not succeed on-chain — nothing credited.' },
};

/** A clock time on the desk's clock, for the step line. */
function at(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' }); // time-ok: the step's clock time on the Dubai clock, beside its word
}

/**
 * Every step the money took, in one line, each with its time: the operator,
 * 21 Oct 2026 — "all the steps customer money follows" on the dashboard.
 * The steps not yet taken are not shown; the one it waits on is named.
 */
function stepsOf(d: DepositRow): string {
  const steps: string[] = [`Seen ${at(d.detectedAt)}`];
  if (d.confirmedAt) steps.push(`Confirmed ${at(d.confirmedAt)}`);
  if (d.heldAt && d.status === 'held') steps.push(`Held ${at(d.heldAt)}`);
  if (d.kytRatedAt || d.screenedAt) steps.push(`Screened ${d.kytVerdict ? (RESULT_WORD[d.kytVerdict] ?? d.kytVerdict) : ''} ${at(d.kytRatedAt ?? d.screenedAt)}`.replace(/\s+/g, ' '));
  if (d.pool && ['routed', 'swept'].includes(d.status)) steps.push(`Routed to ${POOL_WORD[d.pool] ?? d.pool}`);
  const sw = d.sweep;
  if (sw) {
    if (sw.status === 'queued') steps.push('Waiting for you to start signing');
    else if (sw.status === 'activating') steps.push('Preparing the address on-chain — about a minute');
    else if (sw.status === 'expired') steps.push('Card lapsed — rebuilt when you start signing');
    // The card's own word (Astra round c, C4): one state, the same one the counts and the link read.
    else if (sw.status === 'awaiting_approval') steps.push(cardStep(cardStateOf(sw), sw.builtAt));
    else if (sw.status === 'energizing') steps.push('Signed — readying the fee, then sending');
    else if (sw.status === 'broadcast') steps.push(`Sent ${at(sw.broadcastAt)}`);
    else if (sw.status === 'swept') steps.push(`Sent ${at(sw.broadcastAt)}`, `Done ${at(sw.completedAt)}`);
    else if (sw.status === 'failed') steps.push('Failed — needs a look');
  }
  return steps.join(' · ');
}

/** The step line for a sweep at its card, by the card's state. */
function cardStep(state: CardState | null, builtAt: string | null): string {
  switch (state) {
    case 'open': return `Card ready ${at(builtAt)} — sign it in Approvals`;
    case 'signed': return 'Signed — the desk sends it shortly';
    case 'expired': return 'Card lapsed — rebuilt when you start signing';
    case 'replaced': return 'Card replaced — the desk builds a new one';
    default: return 'No card yet — the desk builds it';
  }
}

/** Where a ready card is signed: its own card in Approvals (#card-<txid>), or the list when the id is not known. */
const cardPath = (sw: { txHash: string | null; card?: { txid: string } | null } | null | undefined) => {
  const txid = sw?.card?.txid ?? sw?.txHash ?? null;
  return txid ? `/custody/approvals#card-${txid}` : '/custody/approvals';
};

/** A sweep in motion on the desk's side: the screen re-reads every 3 s while one is, so a press is answered on screen. */
const IN_MOTION = new Set(['activating', 'energizing', 'broadcast']);

/**
 * The one button that starts a signature: it opens a 30-minute window; the
 * desk wakes, activates a fresh address on-chain when it must (one
 * transaction, about a minute), builds the card and Approvals shows it.
 * The press is answered here (the operator's pass, 23 Oct 2026: "nothing
 * happening after I press start signing"): the button says the window is
 * open, the line under it says what the desk is doing, and this card leaves
 * the screen for the "ready" one once the sweep moves on.
 */
function StartSigning({ demoted = false }: { demoted?: boolean }) {
  const qc = useQueryClient();
  // The window as the desk holds it (pay sheet round 8, #3): the button follows it — open, it waits; closed or
  // lapsed, it is pressable again — instead of staying "Window open" for good after one press.
  const win = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, refetchInterval: 30_000, retry: false });
  const m = useMutation({
    mutationFn: () => api.openApprovalWindow(30),
    onSuccess: () => {
      for (const key of ['approval-window', 'approvals', 'deposits', 'summary']) void qc.invalidateQueries({ queryKey: ['custody', key] });
    },
  });
  // The window read said as it stands (Astra round c, minor): not read yet → waiting; failed → said with its retry,
  // and a cached "open" is not trusted; read → open until its expiry, or closed.
  const read = win.isError ? 'failed' : win.data === undefined ? 'reading' : 'read';
  const openUntil = read === 'read' ? windowOpenUntil(win.data) : null;
  return (
    <div className="mt-2">
      {/* One gold button per screen: while a card is already ready, the way to it is the gold one. */}
      <button className={`${demoted ? 'btn-secondary' : 'btn-primary'} min-h-[44px] text-body`} disabled={m.isPending || openUntil !== null || read === 'reading'} onClick={() => m.mutate()}>{m.isPending ? 'Starting…' : openUntil ? 'Window open' : 'Start signing'}</button>
      <span className="ml-2 text-meta text-tg-hint">{read === 'reading' ? 'reading the window…' : openUntil ? `until ${at(openUntil)}` : 'opens a 30-minute window'}</span>
      {read === 'failed' && isCustodySignin(win.error) && <SigninQuiet error={win.error} className="mt-1" />}
      {read === 'failed' && !isCustodySignin(win.error) && (
        <StateLine tone="bad" className="mt-1" more={textOf(win.error)} action={<button type="button" className="btn-link min-h-[44px] text-body" onClick={() => void win.refetch()} disabled={win.isFetching}>{win.isFetching ? 'Reading…' : 'Try again'}</button>}>
          Couldn't read the signing window.
        </StateLine>
      )}
      {openUntil && <div className="mt-2"><Pending what="The desk is preparing the cards…" /></div>}
      {m.isError && <StateLine tone="bad" className="mt-1" more={textOf(m.error)}>Couldn't start — try again.</StateLine>}
    </div>
  );
}

/** A routed deposit whose sweep waits for a window: nothing moves until he starts signing. */
const waitsOnHim = (d: DepositRow) => d.status === 'routed' && (d.sweep?.status === 'queued' || d.sweep?.status === 'expired'
  // A card that lapsed or was replaced before the desk's next tick marks the sweep expired: it waits on him too.
  || cardStateOf(d.sweep) === 'expired');

function Row({ d }: { d: DepositRow }) {
  const s = STATUS[d.status] ?? { label: d.status, tone: 'neutral' as PillTone };
  const ratingTone: PillTone = d.kytVerdict === 'exposed' ? 'warn' : d.kytVerdict === 'clean' ? 'good' : 'neutral';
  const of = lookalikeOf(d);
  return (
    <Card tone={s.tone === 'bad' ? 'bad' : s.tone === 'warn' ? 'warn' : undefined}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Value strong>{d.partyName ?? d.partySlug ?? 'Untagged'}</Value>
          <div className="font-mono text-meta text-tg-hint">
            {/* A look-alike sender (address poisoning, KYT's door): red, the warning for its title — no Copy here either. */}
            from {of ? <span className="text-neg" data-address="lookalike" title={`Address poisoning — look-alike of ${of}. Never a payee; never copy.`}>{shortAddress(d.fromAddress)}</span> : shortAddress(d.fromAddress)} · {relativeTime(d.detectedAt)}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-body font-medium tabular">{money(d.amount, d.asset)}</div>
          <Pill tone={s.tone} className="mt-1">{s.label}</Pill>
        </div>
      </div>

      {(d.kytVerdict || d.pool) && (
        <div className="mt-2 text-meta text-tg-hint">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {d.kytVerdict && (
              <span>
                KYT <span className={`font-medium ${ratingTone === 'warn' ? 'text-warn' : ratingTone === 'good' ? 'text-pos' : ''}`}>
                  {RESULT_WORD[d.kytVerdict] ?? d.kytVerdict}
                </span>
                {d.kytHandCheck && <Pill tone="warn" className="ml-1">check by hand</Pill>}
              </span>
            )}
            {d.pool && <span>Pool <span className="font-medium">{POOL_WORD[d.pool] ?? d.pool}</span></span>}
          </div>
          {/* Why: the engine's sources over the operator's lines — never a score. */}
          {d.kytBecause && d.kytBecause.length > 0 && (
            <StateLine className="mt-1" more={<ul className="list-disc pl-4">{d.kytBecause.map((b) => <li key={b}>{b}</li>)}</ul>}>
              {d.kytBecause.length === 1 ? '1 reason' : `${d.kytBecause.length} reasons`}
            </StateLine>
          )}
        </div>
      )}

      {/* KYT's look-alike line, under its rating — said even when no rating is drawn. */}
      <Lookalike d={d} className="mt-1" />
      {/* The lists, as information: shown, never a refusal (the operator, 22 Oct 2026). */}
      {listLines(d).map((line) => (
        // words:ok — the list's own name, as the rating names it
        <StateLine key={line} tone="warn" className="mt-2" more="A listing is information from the rating engine: it names the sender or the money's path. It decides nothing here — the payout rules are the Books' approval, the source and the caps.">{line}</StateLine>
      ))}
      <p className="mt-2 text-meta text-tg-hint">{stepsOf(d)}</p>
      {s.line && <StateLine tone={s.tone} className="mt-2" more={s.more}>{s.line}</StateLine>}
      {/* The press answered on the row (the operator's pass, 23 Oct 2026): the card is ready and here is the way to it; or the desk is still preparing it. */}
      {cardReady(d.sweep) && (
        <StateLine tone="good" className="mt-2" action={<Link to={cardPath(d.sweep)} className="btn-link text-body font-medium">Sign in Approvals</Link>}>Ready in Approvals.</StateLine>
      )}
      {d.sweep?.status === 'activating' && (
        <StateLine className="mt-2" more="A fresh deposit address is activated on the chain before its first sweep — one transaction, about a minute. The card is built right after and shows in Approvals.">Preparing the card — about a minute.</StateLine>
      )}
    </Card>
  );
}

export function Deposits() {
  const q = useQuery({
    queryKey: ['custody', 'deposits'],
    queryFn: () => api.deposits(),
    // Three seconds while a sweep is in motion on the desk's side (an address activating, a card sent), so a press is
    // answered on screen as it happens (the operator's pass, 23 Oct 2026); twenty otherwise.
    refetchInterval: (query) => (query.state.data?.deposits.some((d) => d.sweep && IN_MOTION.has(d.sweep.status)) ? 3_000 : 20_000),
  });

  if (q.isError) return <ReadFailed what="the deposits" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />;
  if (!q.data) return <Pending what="Reading…" />;
  if (q.data.deposits.length === 0) {
    return <Card><EmptyState>Nothing open or recent.</EmptyState></Card>;
  }

  // A Flagged deposit whose case is closed no longer needs him (the server's `open`; absent on an older server).
  const needsAttention = q.data.deposits.filter((d) => (d.open ?? true) && (d.status === 'held' || d.status === 'quarantined'));
  // A list hit is information that must stay visible (the operator, 22 Oct 2026): a
  // listed deposit behind many newer clean ones is said here, with a way to it.
  const listed = listedDeposits(q.data.deposits);
  // One signing window serves every waiting deposit: the action is said once,
  // at the top, not on every row (one gold button per screen — Screens' Astra review, 22 Oct 2026).
  const waiting = q.data.deposits.filter(waitsOnHim).length;
  // The press answered at the top (the operator's pass, 23 Oct 2026): cards ready to sign, with the way to the first; cards the desk is still preparing.
  const ready = q.data.deposits.filter((d) => cardReady(d.sweep));
  const preparing = q.data.deposits.filter((d) => d.status === 'routed' && d.sweep?.status === 'activating').length;

  return (
    <div className="space-y-3">
      {needsAttention.length > 0 && (
        <StateLine tone="warn">{needsAttention.length === 1 ? '1 deposit needs attention.' : `${needsAttention.length} deposits need attention.`}</StateLine>
      )}
      {listed.length > 0 && (
        <Card tone="warn">
          <StateLine tone="warn" more="A listing is information from the rating engine: it names the sender or the money's path. It decides nothing here — the payout rules do (the operator, 22 Oct 2026). Each one is linked below, oldest first, whatever its status.">
            {listed.length === 1 ? '1 deposit carries a list hit — information only.' : `${listed.length} deposits carry list hits — information only.`}
          </StateLine>
          <ul className="mt-2 space-y-1 text-meta">
            {listed.map((d) => (
              <li key={d.id}>
                <a href={`#deposit-${d.id}`} className="underline">{d.partyName ?? d.partySlug ?? 'Untagged'} · {money(d.amount, d.asset)}</a>
                {' — '}{listLines(d).join('; ')}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {ready.length > 0 && (
        <Card>
          <StateLine tone="good" action={<Link to={ready.length === 1 ? cardPath(ready[0]?.sweep) : '/custody/approvals'} className="btn-primary inline-flex min-h-[44px] items-center px-4 text-body">Sign in Approvals</Link>}>
            {ready.length === 1 ? '1 card is ready to sign.' : `${ready.length} cards are ready to sign.`}
          </StateLine>
        </Card>
      )}
      {preparing > 0 && (
        <Card>
          <StateLine more="A fresh deposit address is activated on the chain before its first sweep — one transaction, about a minute. The card is built right after and shows in Approvals.">
            {preparing === 1 ? 'Window open — preparing 1 card.' : `Window open — preparing ${preparing} cards.`}
          </StateLine>
          <div className="mt-2"><Pending what="About a minute…" /></div>
        </Card>
      )}
      {waiting > 0 && (
        <Card>
          <StateLine>{waiting === 1 ? '1 deposit waits for your signature.' : `${waiting} deposits wait for your signature.`}</StateLine>
          <StartSigning demoted={ready.length > 0} />
        </Card>
      )}
      {q.data.deposits.map((d) => <div key={d.id} id={`deposit-${d.id}`}><Row d={d} /></div>)}
    </div>
  );
}
